import { useContext, useEffect } from 'react';
import {
  useParams,
  useRouteMatch,
  generatePath,
  useHistory,
  useLocation
} from 'react-router-dom';
import Loader from 'components/loader/Loader';
import EditTestResultCard from 'containers/partials/cards/EditTestResultCard';
import useEdit from 'hooks/utils/useEdit';
import {
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE,
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_QUESTIONNAIRE_TESTDATA,
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS,
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS_TESTDATA,
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS_TESTDATA_EDIT
} from 'routes/RouteList';
import { useSessionContext } from 'contexts/SessionContext';
import { useTestDataContext } from 'contexts/TestDataContext';
import { Panel } from 'components/panel';
import { StoreContext } from 'index';
import usePostMessage from 'hooks/utils/usePostMessage';
import { useIntl } from 'react-intl';
import messages from 'messages';
import { getLocalDateFormat } from 'utils/date';

const Questionnaire = () => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId, sessionId, testDataId } = useParams();
  const { push } = useHistory();
  const intl = useIntl();
  const { sendMessage } = usePostMessage();

  let location = useLocation();
  const { session, sessionLoading } = useSessionContext();
  const {
    testData,
    fetchTestData,
    testDataLoading,
    activeDate,
    unsetAllData,
    setNewActiveDate
  } = useTestDataContext();
  const active = useRouteMatch([
    ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE,
    ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS
  ]);
  const isSessionRootView = useRouteMatch({
    path: ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE,
    strict: true,
    exact: true
  });
  const isQuestionnairView = useRouteMatch({
    path: [
      ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE,
      ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_QUESTIONNAIRE_TESTDATA
    ],
    strict: true
  });
  const [edit, setEdit] = useEdit(
    !!useRouteMatch(
      [ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS_TESTDATA_EDIT],
      true
    )
  );

  useEffect(() => {
    const data = {
      title: session?.name,
      description: intl.formatMessage(messages.sessionsQuestionnairesSubtitle, {
        startdate: getLocalDateFormat(session?.startdate),
        enddate: getLocalDateFormat(session?.enddate)
      }),
      canGoBack: true
    };
    sendMessage({ message: data });
  }, [session]);

  useEffect(() => {
    if (session?.id !== sessionId) {
      return;
    }

    if ((!testDataId && session && isQuestionnairView) || isSessionRootView) {
      let testData;

      if (session.isRecurring) {
        if (!activeDate) {
          const today = new Date();
          setNewActiveDate(today);
          testData = session.getTestDataByDate(user.id, today);
        } else {
          testData = session.getTestDataByDate(user.id, activeDate);
        }
      } else {
        testData = session.getLastTestData(user.id);
      }

      if (testData?.id) {
        const selectedPath = session.isRecurring
          ? ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS_TESTDATA
          : testData.finishedPercentage > 0
            ? ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS_TESTDATA
            : ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_QUESTIONNAIRE_TESTDATA;
        push(
          generatePath(selectedPath, {
            entityId,
            sessionId,
            testDataId: testData.id
          })
        );
      } else {
        unsetAllData();
      }
    } else if (testDataId && session && isQuestionnairView) {
      if (!testData.id || testData.id !== testDataId) {
        fetchTestData(session.id, testDataId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testDataId, session, location]);

  if (sessionLoading || testDataLoading) {
    return (
      <Panel active={active?.isExact}>
        <Loader />
      </Panel>
    );
  }
  return (
    <Panel active={active?.isExact}>
      {session && (
        <EditTestResultCard
          entityId={entityId}
          personId={user.id}
          edit={edit}
          rootPath={generatePath(
            ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE,
            {
              entityId,
              sessionId,
              sessionsTab: 'tests',
              personId: user.id
            }
          )}
        />
      )}
    </Panel>
  );
};

export default Questionnaire;
